import React, { FunctionComponent, useRef, useState, ReactElement } from 'react'
import { injectIntl, InjectedIntlProps } from 'react-intl'
import { EventSection } from 'src/components/calendar/eventSection'
import { LoadingContent } from 'src/components/loading-content/LoadingContent'
import { LoadingPlaceholder } from 'src/components/loading-content/LoadingPlaceholder'
import { Carousel } from './carousel/Carousel'
import { MAX_IMAGES_TO_SHOW, PHOTOS_ID } from './constants'

const style = require('./AdditionalImages.css')
import locale from './locale.js'

interface Props{
  loading: boolean,
  eventData: any
}

const Placeholder = () => (
  <LoadingPlaceholder modifierClass={style.placeholder}>
    <LoadingContent width="170px" height="25px" borderRadius="rounder" />
    <LoadingPlaceholder modifierClass={style.imagePlaceholder}>
      <LoadingContent width="100%" height="100%" borderRadius="sharper" />
    </LoadingPlaceholder>
  </LoadingPlaceholder>
)

const AdditionalImagesComponent: FunctionComponent<Props & InjectedIntlProps> = ({
  loading,
  eventData,
  intl: { formatMessage },
}) => {
  const [slideShow, setSlideShow] = useState(false)
  const [slideShowIndex, setslideShowIndex] = useState(0)
  const additionalImages = useRef(null)

  if (loading) return <Placeholder />

  if (!eventData.allImages || eventData.allImages.length === 0) {
    return null
  }

  const closeCarousel = () => {
    scrollIntoView()
    setSlideShow(false)
    setslideShowIndex(0)
  }

  const openCarousel = (idx: number) => {
    window.scrollTo(0, 0)
    setSlideShow(true)
    setslideShowIndex(idx)
  }

  const scrollIntoView = () => {
    additionalImages.current.scrollIntoView(true)
    window.scrollBy(0, -150)
  }

  const renderMore = (idx: number): boolean => {
    return (idx + 1) === MAX_IMAGES_TO_SHOW && eventData.allImages.length > MAX_IMAGES_TO_SHOW
  }

  const renderImages = (): ReactElement<any> => {
    return eventData.allImages.map((image: any, idx: number) => {
      if (idx > (MAX_IMAGES_TO_SHOW - 1)) return

      const shouldRenderMore = renderMore(idx)
      return (
        <div key={idx} onClick={() => openCarousel(idx)} className={shouldRenderMore ? style['container--more'] : style.container}>
          <img className={shouldRenderMore ? style['image--more'] : style.image} src={image.url.replace('160x160', '316x316')} />
          {shouldRenderMore ? <div className={style['label--more']}>{formatMessage(locale.morePhotos, { count: eventData.allImages.length - (MAX_IMAGES_TO_SHOW - 1) })}</div> : null}
        </div>
      )
    })
  }

  return (
    <EventSection sectionId={PHOTOS_ID} heading={formatMessage(locale.photos, { numPhotos: eventData.allImages.length })}>
      <div>
        {slideShow ?
          <Carousel
            closeCarousel={closeCarousel}
            images={eventData.allImages}
            open={slideShow}
            index={slideShowIndex}
          /> : null}

        <section style={{ position: 'relative' }}>
          <div ref={additionalImages} className={style.additionalImages}>
            <div className={style.body}>
              {renderImages()}
            </div>
          </div>
        </section>
      </div>
    </EventSection>
  )
}

export const AdditionalImages = injectIntl(AdditionalImagesComponent)
